import Vue from 'vue';
import Component from 'vue-class-component';
import {Watch} from 'vue-property-decorator';
import {namespace} from 'vuex-class';
import {IDriver, IRouteCalculationStop, IRouteOrder, IRouteParsedLeg, IRouteStartingLocation, IRouteStop} from '@/model/Route/routeInterfaces';
import {IChannel} from '@/model/Route/routeInterfaces';
import {IOnOffHandlers} from 'bonomo/dist';
import {regionForCity} from '@/helpers/groupedRoutingRegions';

const routeModule = namespace('routeModule');

@Component
export class RouteFormMixin extends Vue {
  private readonly BASE_CHANNEL_ID = 1;

  @routeModule.Getter('drivers')           protected drivers!: IDriver[];
  @routeModule.Getter('ordersForRouting')  protected ordersForRouting!: IRouteOrder[];
  @routeModule.Getter('startingLocations') protected startingLocations!: IRouteStartingLocation[];
  @routeModule.Getter('channels')          protected channels!: IChannel[];
  @routeModule.Action('fetchDrivers')            protected fetchDrivers!: () => any;
  @routeModule.Action('fetchOrdersForRouting')   protected fetchOrdersForRouting!: () => any;
  @routeModule.Action('fetchStartingLocations')  protected fetchStartingLocations!: () => any;
  @routeModule.Action('fetchChannels')           protected fetchChannels!: () => any;

  protected loading: boolean = true;
  protected saving: boolean = false;
  protected selectedDriverId: number | null = null;
  protected selectedChannelIds: number[] = [];
  protected selectedCities: string[] = [];
  protected selectedOrders: IRouteOrder[] = [];
  protected selectedStartingLocationId: number | null = null;
  protected selectedStartingLocation: IRouteStartingLocation | null = null;
  protected startingLocationDialog: boolean = false;
  protected draggableStops: IRouteStop[] = [];
  protected extraStopDialog: boolean = false;
  protected editingExtraStopKey: string | null = null;
  protected ons: IOnOffHandlers = {};

  // Override in subclass to control the pool of available orders.
  // Create uses ordersForRouting directly; Edit merges routeOrders into it.
  protected get ordersPool(): IRouteOrder[] {
    return this.ordersForRouting;
  }

  protected get driverItems(): Array<{text: string; value: number}> {
    return this.drivers.map(function(d) { return {text: d.name, value: d.id}; });
  }

  protected get startingLocationItems(): Array<{text: string; value: number}> {
    return this.startingLocations.map(function(loc) {
      return {text: loc.name + ' — ' + loc.address, value: loc.id};
    });
  }

  protected get selectedDriver(): IDriver | null {
    return this.drivers.find((d) => d.id === this.selectedDriverId) || null;
  }

  protected get driverScopedChannelIds(): number[] {
    if (!this.selectedDriver || !this.selectedDriver.channel_id) {
      return [];
    }
    return Array.from(new Set([this.selectedDriver.channel_id, this.BASE_CHANNEL_ID]));
  }

  protected get allChannelItems(): Array<{text: string; value: number}> {
    const seen: {[key: number]: string} = {};
    this.channels.forEach((ch) => {
      seen[ch.id] = ch.name;
    });
    if (this.selectedDriver && this.selectedDriver.channel_id && this.selectedDriver.channel_name) {
      seen[this.selectedDriver.channel_id] = this.selectedDriver.channel_name;
    }
    this.ordersPool.forEach((order) => {
      if (order.channel_id && order.channel_name) {
        seen[order.channel_id] = order.channel_name;
      }
    });
    return Object.keys(seen).map((id) => {
      const numId = Number(id);
      return {text: seen[numId], value: numId};
    });
  }

  protected get driverScopedChannelLabels(): string[] {
    if (this.selectedChannelIds.length === 0) { return []; }
    const labelsById: {[key: number]: string} = {};
    if (this.selectedDriver && this.selectedDriver.channel_id && this.selectedDriver.channel_name) {
      labelsById[this.selectedDriver.channel_id] = this.selectedDriver.channel_name;
    }
    this.ordersPool.forEach((order) => {
      if (order.channel_name) { labelsById[order.channel_id] = order.channel_name; }
    });
    return this.selectedChannelIds
      .map((id) => labelsById[id] || null)
      .filter((label): label is string => !!label);
  }

  protected get driverScopedOrdersPool(): IRouteOrder[] {
    if (this.selectedChannelIds.length === 0) { return this.ordersPool; }
    return this.ordersPool.filter((order) => this.selectedChannelIds.indexOf(order.channel_id) !== -1);
  }

  protected get cityRegionGroups(): Array<{region: string; cities: string[]; mapped: boolean}> {
    const citiesByNormalized: {[normalized: string]: string} = {};
    this.driverScopedOrdersPool.forEach((order) => {
      const city = order.city ? order.city.trim() : '';
      if (!city) { return; }
      const normalized = this.normalizeCity(city);
      if (!citiesByNormalized[normalized]) { citiesByNormalized[normalized] = city; }
    });
    const cities = Object.values(citiesByNormalized);

    const citiesByRegion: {[region: string]: {cities: string[]; mapped: boolean}} = {};
    cities.forEach((city) => {
      const mappedRegion = regionForCity(city);
      const region = mappedRegion || city;
      if (!citiesByRegion[region]) { citiesByRegion[region] = {cities: [], mapped: !!mappedRegion}; }
      citiesByRegion[region].cities.push(city);
    });

    return Object.keys(citiesByRegion).sort((a, b) => a.localeCompare(b)).map((region) => ({
      region,
      cities: citiesByRegion[region].cities.sort((a, b) => a.localeCompare(b)),
      mapped: citiesByRegion[region].mapped,
    }));
  }

  protected get cityNames(): string[] {
    return this.cityRegionGroups.reduce((names: string[], group) => names.concat(group.cities), []);
  }

  protected get cityItems(): Array<{type: 'region' | 'city'; text: string; value: string}> {
    const items: Array<{type: 'region' | 'city'; text: string; value: string}> = [];
    this.cityRegionGroups.forEach((group) => {
      if (group.mapped) {
        items.push({type: 'region', text: group.region, value: 'region::' + group.region});
      }
      group.cities.forEach((city) => {
        items.push({type: 'city', text: city, value: city});
      });
    });
    return items;
  }

  protected regionCities(region: string): string[] {
    const group = this.cityRegionGroups.find((g) => g.region === region);
    return group ? group.cities : [];
  }

  protected isCitySelected(city: string): boolean {
    const normalized = this.normalizeCity(city);
    return this.selectedCities.some((c) => this.normalizeCity(c) === normalized);
  }

  protected isRegionFullySelected(region: string): boolean {
    const cities = this.regionCities(region);
    return cities.length > 0 && cities.every((city) => this.isCitySelected(city));
  }

  protected isRegionPartiallySelected(region: string): boolean {
    const cities = this.regionCities(region);
    return cities.some((city) => this.isCitySelected(city)) && !this.isRegionFullySelected(region);
  }

  protected toggleCitySelection(city: string) {
    if (this.isCitySelected(city)) {
      const normalized = this.normalizeCity(city);
      this.selectedCities = this.selectedCities.filter((c) => this.normalizeCity(c) !== normalized);
    } else {
      this.selectedCities = this.selectedCities.concat([city]);
    }
  }

  protected toggleRegionSelection(region: string) {
    const cities = this.regionCities(region);
    if (this.isRegionFullySelected(region)) {
      const normalizedRegionCities = cities.map((city) => this.normalizeCity(city));
      this.selectedCities = this.selectedCities.filter((c) => normalizedRegionCities.indexOf(this.normalizeCity(c)) === -1);
    } else {
      const missing = cities.filter((city) => !this.isCitySelected(city));
      this.selectedCities = this.selectedCities.concat(missing);
    }
  }

  protected onCityListItemClick(item: {type: 'region' | 'city'; text: string; value: string}) {
    if (item.type === 'region') {
      this.toggleRegionSelection(item.text);
    } else {
      this.toggleCitySelection(item.text);
    }
  }

  protected get filteredAvailableOrders(): IRouteOrder[] {
    const selectedIds = this.selectedOrders.map(function(o) { return o.id; });
    let orders = this.driverScopedOrdersPool.filter(function(o) { return selectedIds.indexOf(o.id) === -1; });
    if (this.selectedCities.length > 0) {
      const normalizedSelected = this.selectedCities.map((c) => this.normalizeCity(c));
      orders = orders.filter((order) => normalizedSelected.indexOf(this.normalizeCity(order.city)) !== -1);
    }
    return orders;
  }

  protected get canSave(): boolean {
    return !!this.selectedDriverId && this.selectedOrders.length > 0 && !this.hasInvalidExtraStops;
  }

  protected get hasInvalidExtraStops(): boolean {
    return this.draggableStops.some((stop) => !!stop.isExtra && (!stop.name || !stop.address || !stop.lat || !stop.lng));
  }

  protected get editingExtraStop(): IRouteStop | null {
    if (!this.editingExtraStopKey) { return null; }
    return this.draggableStops.find((stop) => stop.locationKey === this.editingExtraStopKey) || null;
  }

  protected get canAdmin(): boolean {
    return this.$canAction('routes/admin');
  }

  // Phones/tablets: the save bar sticks to the bottom of the viewport, right above the app footer.
  protected get compactForm(): boolean {
    return this.$vuetify.breakpoint.smAndDown;
  }

  protected get stickyActionsStyle(): {[key: string]: string} {
    return this.compactForm ? {bottom: this.$vuetify.application.footer + 'px'} : {};
  }

  @Watch('selectedStartingLocationId')
  protected onSelectedStartingLocationIdChanged(newId: number | null) {
    if (newId === null) {
      this.selectedStartingLocation = null;
      return;
    }
    this.selectedStartingLocation = this.startingLocations.find(function(loc) { return loc.id === newId; }) || null;
  }

  @Watch('selectedDriverId')
  protected onSelectedDriverIdChanged() {
    if (this.selectedCities.length === 0) { return; }
    const normalizedAvailable = this.cityNames.map((c) => this.normalizeCity(c));
    this.selectedCities = this.selectedCities.filter((c) => normalizedAvailable.indexOf(this.normalizeCity(c)) !== -1);
  }

  @Watch('driverScopedChannelIds')
  protected onDriverScopedChannelIdsChanged(newIds: number[]) {
    this.selectedChannelIds = newIds.slice();
  }

  @Watch('selectedOrders')
  protected onSelectedOrdersChanged(orders: IRouteOrder[]) {
    this.syncStopsFromOrders(orders);
  }

  protected onStartingLocationSelected(loc: IRouteStartingLocation) {
    this.selectedStartingLocation   = loc;
    this.selectedStartingLocationId = loc.id;
  }

  protected defaultTimeSlotId(order: IRouteOrder): number | null {
    if (order.selected_time_slot) { return order.selected_time_slot.id; }
    if (order.time_slots && order.time_slots.length > 0) { return order.time_slots[0].id; }
    return null;
  }

  protected normalizeCity(value: string | null | undefined): string {
    if (!value) { return ''; }
    return String(value).trim().toLowerCase();
  }

  protected removeOrder(order: IRouteOrder) {
    this.selectedOrders = this.selectedOrders.filter(function(o) { return o.id !== order.id; });
  }

  protected openExtraStopDialog(locationKey: string | null = null) {
    this.editingExtraStopKey = locationKey;
    this.extraStopDialog = true;
  }

  protected addExtraStop(extraStop: {name: string; address: string; lat: number; lng: number}) {
    const key = 'extra::' + String(Date.now());
    this.draggableStops = this.draggableStops.concat([{
      isExtra: true,
      locationKey: key,
      location: extraStop.name,
      city: null,
      orders: [],
      name: extraStop.name,
      address: extraStop.address,
      lat: extraStop.lat,
      lng: extraStop.lng,
    }]);
  }

  protected removeExtraStop(locationKey: string) {
    this.draggableStops = this.draggableStops.filter(function(s) { return s.locationKey !== locationKey; });
  }

  protected onExtraStopSaved(extraStop: {name: string; address: string; lat: number; lng: number}) {
    if (!this.editingExtraStopKey) {
      this.addExtraStop(extraStop);
      return;
    }
    const editingKey = this.editingExtraStopKey;
    this.draggableStops = this.draggableStops.map(function(s) {
      if (s.locationKey !== editingKey) { return s; }
      return {
        isExtra: true,
        locationKey: s.locationKey,
        location: extraStop.name,
        city: null,
        orders: [],
        name: extraStop.name,
        address: extraStop.address,
        lat: extraStop.lat,
        lng: extraStop.lng,
      };
    });
    this.editingExtraStopKey = null;
  }

  protected onExtraStopInput(locationKey: string, value: string) {
    this.draggableStops = this.draggableStops.map(function(s) {
      if (s.locationKey !== locationKey) { return s; }
      return {isExtra: true, locationKey: s.locationKey, location: value, city: null, orders: [], name: value, address: value};
    });
  }

  protected syncStopsFromOrders(orders: IRouteOrder[]) {
    const grouped: {[key: string]: IRouteOrder[]} = {};
    orders.forEach(function(order) {
      const key = (order.location || '') + '||' + (order.city || '');
      if (!grouped[key]) { grouped[key] = []; }
      grouped[key].push(order);
    });

    const newStops: IRouteStop[] = [];
    const seenKeys: {[key: string]: boolean} = {};

    this.draggableStops.forEach(function(stop) {
      if (stop.isExtra) {
        newStops.push(stop);
      } else if (grouped[stop.locationKey]) {
        newStops.push({locationKey: stop.locationKey, location: stop.location, city: stop.city, orders: grouped[stop.locationKey]});
        seenKeys[stop.locationKey] = true;
      }
    });

    Object.keys(grouped).forEach(function(key) {
      if (!seenKeys[key]) {
        const ordersInStop = grouped[key];
        newStops.push({locationKey: key, location: ordersInStop[0].location || '', city: ordersInStop[0].city || null, orders: ordersInStop});
      }
    });

    this.draggableStops = newStops;
  }

  protected buildRouteCalculationStops(): IRouteCalculationStop[] {
    return this.draggableStops.reduce((stops: IRouteCalculationStop[], stop) => {
      if (stop.isExtra) {
        if (stop.lat && stop.lng && stop.address) {
          stops.push({
            type: 'extra',
            name: stop.name || stop.address,
            address: stop.address,
            location: {lat: stop.lat, lng: stop.lng},
          });
        }
        return stops;
      }
      stops.push({type: 'order', order_ids: stop.orders.map((o) => o.id)});
      return stops;
    }, []);
  }

  protected buildExtraStopsPayload(parsedLegs: IRouteParsedLeg[]) {
    return parsedLegs.reduce((extraStops: Array<{name: string; address: string; lat: number; lng: number; leg_index: number}>, leg, index) => {
      if (leg.extra_stop_address && leg.extra_stop_address.trim() !== '') {
        extraStops.push({
          name:      leg.extra_stop_name || leg.extra_stop_address,
          address:   leg.extra_stop_address.trim(),
          lat:       leg.endpoint_lat,
          lng:       leg.endpoint_lng,
          leg_index: index,
        });
      }
      return extraStops;
    }, []);
  }

  protected routeSaveErrorMessage(e: any): string {
    const message = e && e.message ? String(e.message) : '';
    if (message.indexOf('ROUTE_DIRECTIONS_FAILED:') === 0) {
      return this.$t('routes.create.route_address_error') as string;
    }
    return this.$t('common.errorHappened') as string;
  }

  protected onSaveError() {
    this.saving = false;
  }

  beforeDestroy() {
    this.$offs(this.ons);
  }
}
